import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from './Modal.jsx';
import { useWindowWidth } from '../hooks/useWindowWidth.js';
import { createTicket } from '../services/feedbackService.js';

const MAX_ATTACHMENTS = 4;

const TYPE_OPTIONS = [
  { value: 'broken',    label: "Something's broken" },
  { value: 'confusing', label: "Something's confusing" },
  { value: 'missing',   label: "Something's missing" },
  { value: 'other',     label: 'Other' },
];

function ClipboardIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
    </svg>
  );
}

function CloseCornerButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Close"
      style={{
        position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%',
        border: 'none', background: 'transparent', color: 'var(--color-text-muted)',
        cursor: 'pointer', fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      &times;
    </button>
  );
}

export default function FeedbackModal({ onClose }) {
  const location = useLocation();
  const { isMobile } = useWindowWidth();
  const [type, setType]       = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles]     = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr]         = useState('');
  const [sent, setSent]       = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    function onPaste(e) {
      const items = e.clipboardData?.items ?? [];
      const images = [];
      for (const item of items) {
        if (item.type?.startsWith('image/')) {
          const blob = item.getAsFile();
          if (blob) images.push(blob);
        }
      }
      if (images.length) {
        setFiles(prev => [...prev, ...images].slice(0, MAX_ATTACHMENTS));
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  function addFiles(list) {
    const picked = Array.from(list ?? []).filter(f => f.type?.startsWith('image/'));
    if (picked.length) setFiles(prev => [...prev, ...picked].slice(0, MAX_ATTACHMENTS));
  }

  function handleFilePick(e) {
    addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleDragOver(e) { e.preventDefault(); setDragActive(true); }
  function handleDragLeave(e) { e.preventDefault(); setDragActive(false); }
  function handleDrop(e) {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer?.files);
  }

  function removeFile(idx) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSend() {
    setSending(true);
    setErr('');
    try {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('message', message);
      formData.append('pagePath', location.pathname + location.search);
      files.forEach(f => formData.append('attachments', f, f.name || 'screenshot.png'));
      await createTicket(formData);
      setSent(true);
    } catch (ex) {
      setErr(ex?.response?.data?.error ?? ex.message ?? 'Failed to send feedback');
    }
    setSending(false);
  }

  const canSend = type && message.trim() && !sending;

  if (sent) {
    return (
      <Modal title="Feedback sent" onClose={onClose} hideHeader>
        <div style={{ position: 'relative', padding: '8px 4px' }}>
          <CloseCornerButton onClick={onClose} />
          <h2 style={{ margin: '0 24px 12px 0', fontSize: '1.0625rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>
            Feedback sent
          </h2>
          <p style={{ color: 'var(--color-text-primary)' }}>
            Thanks — your feedback was sent. You&apos;ll get an email when it&apos;s resolved.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="ns-btn ns-btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Send feedback" onClose={onClose} hideHeader>
      <div style={{ position: 'relative', padding: '4px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <CloseCornerButton onClick={onClose} />

        <div style={{ marginRight: 24 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: '1.0625rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>
            Send feedback
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Tell us what&apos;s broken, confusing, or missing on this screen — it goes straight to the team.
          </p>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4 }}>
            Type
          </label>
          <select
            value={type}
            onChange={e => setType(e.target.value)}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore=""
            data-form-type="other"
            data-bwignore=""
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)' }}
          >
            <option value="">Select…</option>
            {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 4 }}>
            Your feedback
          </label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
            placeholder="What happened, or what would make this better?"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-1p-ignore=""
            data-form-type="other"
            data-bwignore=""
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', resize: 'vertical' }}
          />
        </div>

        <div>
          {isMobile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={files.length >= MAX_ATTACHMENTS}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '14px 12px', borderRadius: 8, border: '2px dashed var(--color-border)',
                background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)',
                fontSize: 13, fontWeight: 600,
                cursor: files.length >= MAX_ATTACHMENTS ? 'default' : 'pointer',
                opacity: files.length >= MAX_ATTACHMENTS ? 0.6 : 1,
              }}
            >
              <ClipboardIcon />
              Add a screenshot
            </button>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragActive ? 'var(--color-success)' : 'var(--color-border)'}`,
                borderRadius: 8,
                padding: '16px 12px',
                textAlign: 'center',
                background: dragActive ? 'var(--color-success-bg)' : 'var(--color-bg-primary)',
                color: 'var(--color-text-muted)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', color: 'var(--color-text-muted)', marginBottom: 6 }}>
                <ClipboardIcon />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                Paste a screenshot with Ctrl + V
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
                Take one with Win + Shift + S, then paste anywhere in this window — you don&apos;t need to click here first. You can also drag a file in.
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 12 }}>
              {files.map((f, idx) => (
                <div key={idx} style={{ position: 'relative', width: 64, height: 64, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                  <img src={URL.createObjectURL(f)} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button
                    onClick={() => removeFile(idx)}
                    aria-label={`Remove ${f.name}`}
                    style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'var(--color-danger)', color: 'var(--color-accent-btn-text)', cursor: 'pointer', fontSize: 11, lineHeight: 1 }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: isMobile ? 'flex-end' : 'space-between', alignItems: 'center', marginTop: 8 }}>
            {!isMobile && (
              files.length < MAX_ATTACHMENTS ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-text-muted)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  or choose a file
                </button>
              ) : <span />
            )}
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-success-text)' }}>
              {MAX_ATTACHMENTS - files.length} of {MAX_ATTACHMENTS} left
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilePick}
            style={{ display: 'none' }}
          />
        </div>

        <div style={{ fontSize: 13, fontFamily: 'monospace', color: 'var(--color-success-text)' }}>
          On: {location.pathname}{location.search}
        </div>

        {err && <div style={{ color: 'var(--color-danger)', fontSize: 13 }}>{err}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button className="ns-btn ns-btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="ns-btn ns-btn-primary" onClick={handleSend} disabled={!canSend}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
